import React from 'react';
import PropTypes from 'prop-types';
import {SectionTitle, InfoItem} from '@/components/molecules';
import {getFormattedDate} from '@/utils/commons';
import {
  REWARD_TYPES,
  REWARD_CARD_TYPES,
  isInventoryManagedRewardType
} from '@/utils/constants/rewardsConstants';
import {
  SECTION_TITLES,
  FIELD_LABELS,
  DEFAULT_VALUES,
  CSS_CLASSES,
  DATE_FORMATS
} from '../common/ReviewConstants';

// Helper to get inventory value based on card variant
const getInventoryValue = (
  formData,
  cardVariant,
  digitalField,
  physicalField,
  defaultField
) => {
  if (formData?.unlimitedInventory) {
    return DEFAULT_VALUES.UNLIMITED;
  }
  if (cardVariant === REWARD_CARD_TYPES.Digital) {
    return formData?.[digitalField] || DEFAULT_VALUES.FALLBACK;
  }
  if (cardVariant === REWARD_CARD_TYPES.Physical) {
    return formData?.[physicalField] || DEFAULT_VALUES.FALLBACK;
  }
  return formData?.[defaultField] || DEFAULT_VALUES.FALLBACK;
};

// Sub-component for VISA_CARD with Both card type
const VisaCardBothAttributes = ({
  formData,
  getQuotaValue,
  getLeftoverInventoryValue
}) => (
  <>
    <InfoItem
      label='Quota Digital Card'
      value={getQuotaValue(REWARD_CARD_TYPES.Digital)}
    />
    <InfoItem
      label='Limit per Store - Digital Card'
      value={formData?.limitPerStoreDigitalCard || DEFAULT_VALUES.FALLBACK}
    />
    <InfoItem
      label='Leftover Inventory - Digital Card'
      value={getLeftoverInventoryValue(REWARD_CARD_TYPES.Digital)}
    />
    <InfoItem
      label='Quota Physical Card'
      value={getQuotaValue(REWARD_CARD_TYPES.Physical)}
    />
    <InfoItem
      label='Limit per Store Physical Card'
      value={formData?.limitPerStorePhysicalCard || DEFAULT_VALUES.FALLBACK}
    />
    <InfoItem
      label='Leftover Inventory - Physical Card'
      value={getLeftoverInventoryValue(REWARD_CARD_TYPES.Physical)}
    />
    <InfoItem
      label={FIELD_LABELS.ATTRIBUTES.DISPLAY_OUT_OF_STOCK}
      value={
        formData?.displayOutOfStock ? DEFAULT_VALUES.YES : DEFAULT_VALUES.NO
      }
    />
    <InfoItem
      label={FIELD_LABELS.ATTRIBUTES.UNLIMITED_INVENTORY}
      value={
        formData?.unlimitedInventory ? DEFAULT_VALUES.YES : DEFAULT_VALUES.NO
      }
    />
    <InfoItem
      label={FIELD_LABELS.ATTRIBUTES.IN_STOCK}
      value={formData?.inStock ? DEFAULT_VALUES.YES : DEFAULT_VALUES.NO}
    />
    <InfoItem
      label={FIELD_LABELS.DISPLAY.SHOWN_ON_HOMEPAGE}
      value={formData?.showOnHomePage ? DEFAULT_VALUES.YES : DEFAULT_VALUES.NO}
    />
  </>
);

VisaCardBothAttributes.propTypes = {
  formData: PropTypes.object.isRequired,
  getQuotaValue: PropTypes.func.isRequired,
  getLeftoverInventoryValue: PropTypes.func.isRequired
};

// Sub-component for standard attributes
const StandardAttributes = ({
  formData,
  hasInventoryFields,
  showMaxQuantityPerOrder,
  isBonusProducts,
  getQuotaValue,
  getLeftoverInventoryValue
}) => (
  <>
    {hasInventoryFields && (
      <>
        <InfoItem
          label={FIELD_LABELS.ATTRIBUTES.QUOTA}
          value={getQuotaValue()}
        />
        <InfoItem
          label={FIELD_LABELS.ATTRIBUTES.LEFTOVER_INVENTORY}
          value={getLeftoverInventoryValue()}
        />
        <InfoItem
          label={FIELD_LABELS.ATTRIBUTES.LIMIT_PER_STORE}
          value={formData?.limitPerStore || DEFAULT_VALUES.FALLBACK}
        />
      </>
    )}
    {showMaxQuantityPerOrder && (
      <InfoItem
        label={FIELD_LABELS.ATTRIBUTES.MAX_QUANTITY_PER_ORDER}
        value={formData?.maxQtyPerOrder || DEFAULT_VALUES.FALLBACK}
      />
    )}
    {isBonusProducts && (
      <InfoItem
        label={FIELD_LABELS.DISPLAY.ALERT_DISPLAY_COUNT}
        value={
          formData?.alertDisplayCount === null ||
          formData?.alertDisplayCount === undefined ||
          formData?.alertDisplayCount === ''
            ? DEFAULT_VALUES.FALLBACK
            : formData?.alertDisplayCount
        }
      />
    )}
    <InfoItem
      label={FIELD_LABELS.ATTRIBUTES.DISPLAY_OUT_OF_STOCK}
      value={
        formData?.displayOutOfStock ? DEFAULT_VALUES.YES : DEFAULT_VALUES.NO
      }
    />
    {hasInventoryFields && (
      <InfoItem
        label={FIELD_LABELS.ATTRIBUTES.UNLIMITED_INVENTORY}
        value={
          formData?.unlimitedInventory ? DEFAULT_VALUES.YES : DEFAULT_VALUES.NO
        }
      />
    )}
    <InfoItem
      label={FIELD_LABELS.ATTRIBUTES.IN_STOCK}
      value={formData?.inStock ? DEFAULT_VALUES.YES : DEFAULT_VALUES.NO}
    />
    <InfoItem
      label={FIELD_LABELS.DISPLAY.SHOWN_ON_HOMEPAGE}
      value={formData?.showOnHomePage ? DEFAULT_VALUES.YES : DEFAULT_VALUES.NO}
    />
  </>
);

StandardAttributes.propTypes = {
  formData: PropTypes.object.isRequired,
  hasInventoryFields: PropTypes.bool.isRequired,
  showMaxQuantityPerOrder: PropTypes.bool.isRequired,
  isBonusProducts: PropTypes.bool.isRequired,
  getQuotaValue: PropTypes.func.isRequired,
  getLeftoverInventoryValue: PropTypes.func.isRequired
};

const AttributesSection = ({formData}) => {
  const hasInventoryFields = isInventoryManagedRewardType(
    formData?.rewardType,
    formData?.cardType
  );
  const isVisaCardBoth =
    formData?.rewardType === REWARD_TYPES.VISA_CARD &&
    formData?.cardType === REWARD_CARD_TYPES.Both;
  const isBonusProducts = formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const showMaxQuantityPerOrder =
    formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_DISCOUNT ||
    formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;

  const getLeftoverInventoryValue = (cardVariant = null) =>
    getInventoryValue(
      formData,
      cardVariant,
      'leftOverInventoryDigitalCard',
      'leftOverInventoryPhysicalCard',
      'leftOverInventory'
    );

  const getQuotaValue = (cardVariant = null) =>
    getInventoryValue(
      formData,
      cardVariant,
      'quotaDigitalCard',
      'quotaPhysicalCard',
      'quota'
    );

  return (
    <div className={CSS_CLASSES.LAYOUT.SECTION_WRAPPER}>
      <div className={CSS_CLASSES.COMMON.SECTION_BORDER}>
        <SectionTitle
          id='availability'
          label={SECTION_TITLES.ATTRIBUTES.AVAILABILITY}
        />
      </div>
      <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
        <InfoItem
          label={FIELD_LABELS.ATTRIBUTES.START_DATE}
          value={getFormattedDate(
            formData?.startDate,
            DATE_FORMATS.DEFAULT.showTime,
            DATE_FORMATS.DEFAULT.separator,
            DATE_FORMATS.DEFAULT.format
          )}
        />
        <InfoItem
          label={FIELD_LABELS.ATTRIBUTES.END_DATE}
          value={getFormattedDate(
            formData?.endDate,
            DATE_FORMATS.DEFAULT.showTime,
            DATE_FORMATS.DEFAULT.separator,
            DATE_FORMATS.DEFAULT.format
          )}
        />
      </div>
      <div className={CSS_CLASSES.COMMON.SECTION_BORDER}>
        <SectionTitle
          id='additional_attributes'
          label={SECTION_TITLES.ATTRIBUTES.ADDITIONAL_ATTRIBUTES}
        />
      </div>

      <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
        {isVisaCardBoth ? (
          <VisaCardBothAttributes
            formData={formData}
            getQuotaValue={getQuotaValue}
            getLeftoverInventoryValue={getLeftoverInventoryValue}
          />
        ) : (
          <StandardAttributes
            formData={formData}
            hasInventoryFields={hasInventoryFields}
            showMaxQuantityPerOrder={showMaxQuantityPerOrder}
            isBonusProducts={isBonusProducts}
            getQuotaValue={getQuotaValue}
            getLeftoverInventoryValue={getLeftoverInventoryValue}
          />
        )}
      </div>
    </div>
  );
};

AttributesSection.propTypes = {
  formData: PropTypes.shape({
    rewardType: PropTypes.string,
    cardType: PropTypes.string,
    startDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    endDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    quota: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leftOverInventory: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    limitPerStore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxQtyPerOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quotaDigitalCard: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leftOverInventoryDigitalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    limitPerStoreDigitalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    quotaPhysicalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    leftOverInventoryPhysicalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    limitPerStorePhysicalCard: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    displayOutOfStock: PropTypes.bool,
    unlimitedInventory: PropTypes.bool,
    inStock: PropTypes.bool,
    showOnHomePage: PropTypes.bool,
    alertDisplayCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired
};

export default AttributesSection;
